import { action, computed, makeObservable, observable, reaction } from 'mobx';
import SsoClient from 'telia-sso-client';

import { AuthCustomer, AuthDealer, AuthPerson, SsoClientState } from '../../typings/telia-sso-client';
import config from '../config';
import { SsoClientService } from '../services/SsoClientService';

import { ILanguageStore, Language } from './LanguageStore';
import { IModalStore } from './ModalStore';
import { IRouteStore } from './RouteStore';
import { SsoService } from './SsoService';

export interface ISsoStore {
  readonly customerSelected: boolean;
  readonly isPrivateCustomer: boolean;
  readonly isBusinessCustomer: boolean;
  readonly person: AuthPerson;
  readonly customer: AuthCustomer;
  readonly dealer: AuthDealer;
  readonly isDealer: boolean;
  readonly loading: boolean;
  readonly ssoClient: SsoClient;
  readonly isSsoFrameVisible: boolean;
  readonly ssoState: SsoClientState;

  init(): void;

  iframeUrl(): string;

  doLogout(): void;

  openSsoFrame(): void;

  closeSsoFrame(): void;
}

const HARD_AUTH_LEVEL = 2;

enum SsoEventType {
  LOADING,
  LOGGED_OUT_BY_USER,
  LOGGED_OUT_ON_PAGE_LOAD,
  LOGGED_IN_AS_PERSON_WITHOUT_CUSTOMER_SELECTED,
  LOGGED_IN_AS_PERSON_WITH_CUSTOMER_SELECTED,
  LOGGED_IN_AS_DEALER_WITHOUT_CUSTOMER_SELECTED,
  LOGGED_IN_AS_DEALER_WITH_CUSTOMER_SELECTED
}

export class SsoStore implements ISsoStore {
  @observable
  public isSsoFrameVisible = false;

  @observable
  public ssoState: SsoClientState = {} as any;

  constructor(
    private languageStore: ILanguageStore,
    private ssoClientService: SsoClientService,
    private ssoService: SsoService,
    private routeStore: IRouteStore,
    private modalStore: IModalStore
  ) {
    makeObservable(this);
  }

  @action
  public init(): void {
    const APP_DIIL = 'diil';
    this.ssoClientService.init(
      {
        onLogin: (data: any) => this.handleLogin(data),
        onLogout: () => this.handleLogout(),
        registerCookies: true,
        disableTabSync: true,
        env: config.ssoClientEnvironment
      },
      {
        app: APP_DIIL,
        locale: this.languageStore.currentLanguage,
        autoSelectSelf: true,
        hideB2B: true,
        selectMandate: true
      }
    );

    this.ssoClientService.loadInitialState();

    this.ssoClientService.setLocale(this.languageStore.currentLanguage);
    reaction(
      () => this.languageStore.currentLanguage,
      (lang: Language) => this.ssoClientService.setLocale(lang)
    );
  }

  @action
  public closeSsoFrame(): void {
    this.isSsoFrameVisible = false;
  }

  @action
  public openSsoFrame(): void {
    if (!this.isSsoFrameVisible) {
      this.modalStore.closeModal();
    }
    this.isSsoFrameVisible = true;
  }

  @computed
  public get ssoClient(): SsoClient {
    return this.ssoClientService.ssoClientInstance;
  }

  @computed
  public get customer(): AuthCustomer {
    if (this.customerSelected) {
      return this.ssoState.auth.customer;
    }
    return null;
  }

  @computed
  public get isPrivateCustomer(): boolean {
    return this.customerSelected && this.ssoState.auth.customer.type === 'B2C';
  }

  @computed
  public get isBusinessCustomer(): boolean {
    return this.customerSelected && this.ssoState.auth.customer.type === 'B2B';
  }

  @computed
  public get person(): AuthPerson {
    if (this.customerSelected) {
      return this.ssoState.auth.person;
    }
    return null;
  }

  @computed
  public get dealer(): AuthDealer {
    if (this.isDealer) {
      return this.ssoState.auth.dealer;
    }
    return null;
  }

  @computed
  public get loading(): boolean {
    return SsoStore.getEventType(this.ssoState) === SsoEventType.LOADING;
  }

  @computed
  public get customerSelected(): boolean {
    return this.isCustomerSelected(this.ssoState);
  }

  @computed
  public get isDealer(): boolean {
    const eventType = SsoStore.getEventType(this.ssoState);
    return eventType === SsoEventType.LOGGED_IN_AS_DEALER_WITH_CUSTOMER_SELECTED || eventType === SsoEventType.LOGGED_IN_AS_DEALER_WITHOUT_CUSTOMER_SELECTED;
  }

  public iframeUrl(): string {
    return this.ssoClientService.loginUrl();
  }

  public doLogout(): void {
    this.ssoService.doLogout();
  }

  @action
  private updateSsoState(ssoState: SsoClientState) {
    const previousState = this.ssoState;

    if (this.isLoggedIn(ssoState)) {
      this.ssoClientService.setAuthLevel(null);
    }
    if (SsoStore.isLoggedOut(ssoState)) {
      this.ssoClientService.setAuthLevel(HARD_AUTH_LEVEL);
    }
    if (this.isLoginFlowCompleted(ssoState)) {
      this.closeSsoFrame();
      if (this.isCustomerSelected(previousState)) {
        this.ssoService.refreshPage();
      }
    }

    this.ssoState = ssoState;
  }

  public handleLogin = (ssoState: SsoClientState) => this.updateSsoState(ssoState);

  public handleLogout = () => {
    this.updateSsoState(null);
    this.routeStore.goToExternalUrl(config.headerRedirectUrl);
  };

  private isCustomerSelected(state: SsoClientState) {
    const stateType = SsoStore.getEventType(state);
    return stateType === SsoEventType.LOGGED_IN_AS_PERSON_WITH_CUSTOMER_SELECTED || stateType === SsoEventType.LOGGED_IN_AS_DEALER_WITH_CUSTOMER_SELECTED;
  }

  private isLoginFlowCompleted(state: SsoClientState) {
    const eventType = SsoStore.getEventType(state);
    return (
      eventType === SsoEventType.LOGGED_OUT_BY_USER ||
      eventType === SsoEventType.LOGGED_IN_AS_PERSON_WITH_CUSTOMER_SELECTED ||
      eventType === SsoEventType.LOGGED_IN_AS_DEALER_WITHOUT_CUSTOMER_SELECTED ||
      eventType === SsoEventType.LOGGED_IN_AS_DEALER_WITH_CUSTOMER_SELECTED
    );
  }

  private isLoggedIn(state: SsoClientState) {
    const eventType = SsoStore.getEventType(state);
    return (
      eventType === SsoEventType.LOGGED_IN_AS_DEALER_WITH_CUSTOMER_SELECTED ||
      eventType === SsoEventType.LOGGED_IN_AS_DEALER_WITHOUT_CUSTOMER_SELECTED ||
      eventType === SsoEventType.LOGGED_IN_AS_PERSON_WITH_CUSTOMER_SELECTED ||
      eventType === SsoEventType.LOGGED_IN_AS_PERSON_WITHOUT_CUSTOMER_SELECTED
    );
  }

  private static isLoggedOut(state: SsoClientState) {
    const eventType = SsoStore.getEventType(state);
    return eventType === SsoEventType.LOGGED_OUT_BY_USER || eventType === SsoEventType.LOGGED_OUT_ON_PAGE_LOAD;
  }

  private static getEventType(state: SsoClientState): SsoEventType {
    if (state == null) {
      return SsoEventType.LOGGED_OUT_BY_USER;
    }
    if (state.action !== 'loaded') {
      return SsoEventType.LOADING;
    }
    if (state.auth == null) {
      return SsoEventType.LOGGED_OUT_ON_PAGE_LOAD;
    }
    if (state.auth.dealer != null) {
      if (state.auth.customer != null) {
        return SsoEventType.LOGGED_IN_AS_DEALER_WITH_CUSTOMER_SELECTED;
      } else {
        return SsoEventType.LOGGED_IN_AS_DEALER_WITHOUT_CUSTOMER_SELECTED;
      }
    }
    if (state.auth.authLevel === HARD_AUTH_LEVEL) {
      if (state.auth.customer != null) {
        return SsoEventType.LOGGED_IN_AS_PERSON_WITH_CUSTOMER_SELECTED;
      } else {
        return SsoEventType.LOGGED_IN_AS_PERSON_WITHOUT_CUSTOMER_SELECTED;
      }
    }
    return SsoEventType.LOGGED_OUT_ON_PAGE_LOAD;
  }
}
